import React, { useState, useEffect, useCallback } from 'react';
import { FileText, CheckCircle, AlertCircle, Loader, RefreshCw, Plus, Trash2, ChevronDown } from 'lucide-react';
import { supabase } from './supabaseClient';
import CatalogSearch from './components/CatalogSearch';
import AnexoPreview from './AnexoPreview';
import { PACKAGE_TAX_FORMULA, calcPackageTax, customsConfigComplete, fetchCustomsConfigRow, packageFob } from './utils/customsCalc';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fieldDesc = (item) =>
  item?.description ?? item?.descripcion ?? item?.desc ?? '';

const lbToKg = (lb) => ((parseFloat(lb) || 0) * 0.4536).toFixed(2);

const fmt = (n, dec = 2) =>
  (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const emptyItem = () => ({
  id: 'manual-' + Date.now() + '-' + Math.random().toString(36).slice(2),
  description: '',
  product_name: '',
  quantity: 1,
  unit_value: 0,
  condition: 'NUEVO',
  is_merchandise: false,
});

// A deck's tipo picks its customs_config row: encomienda decks are keyed enc-<id>.
const deckTipo = (key, deck) =>
  deck?.type === 'encomienda' || String(key).startsWith('enc-') ? 'encomienda' : 'casillero';

// Taxes for ONE deck (one package), stamped with the formula version so the
// invoice can tell them from figures saved by the retired formula.
// null when the tipo's customs_config row is missing a rate — never a default.
function deckTaxes(items, config) {
  if (!customsConfigComplete(config)) return null;
  const fob = packageFob(items);
  return { fob, ...calcPackageTax(fob, config), formula: PACKAGE_TAX_FORMULA };
}

// ── Item table ────────────────────────────────────────────────────────────────
function ItemTable({ items, onChange, disabled }) {
  const handleChange = (idx, field, value) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const handleAdd = () => onChange([...items, emptyItem()]);
  const handleDelete = (idx) => onChange(items.filter((_, i) => i !== idx));

  const cellStyle = { padding: '4px 6px' };
  const inputStyle = {
    width: '100%', fontSize: '13px',
    border: '1px solid var(--border)', borderRadius: '4px',
    padding: '4px 6px', background: 'var(--bg)',
  };

  return (
    <div style={{ marginTop: '12px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Descripción</th>
            <th style={{ ...cellStyle, textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', width: '64px' }}>Cant.</th>
            <th style={{ ...cellStyle, textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', width: '96px' }}>Valor USD</th>
            <th style={{ ...cellStyle, textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', width: '84px' }}>Estado</th>
            <th style={{ width: '28px' }} />
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it.id || idx} style={{ borderBottom: '1px solid var(--border-light, #f0f0f0)' }}>
              <td style={cellStyle}>
                <div style={{ pointerEvents: disabled ? 'none' : 'auto', opacity: disabled ? 0.55 : 1 }}>
                  <CatalogSearch
                    value={fieldDesc(it)}
                    placeholder="Descripción para aduana"
                    onChange={val => handleChange(idx, 'description', val)}
                    onSelect={cat => {
                      onChange(items.map((item, i) => i !== idx ? item : {
                        ...item,
                        description:    cat.descripcion,
                        product_name:   cat.descripcion,
                        unit_value:     parseFloat(cat.fob_usd) || item.unit_value,
                        condition:      item.condition || 'NUEVO',
                        is_merchandise: true,
                      }));
                    }}
                  />
                </div>
              </td>
              <td style={cellStyle}>
                <input
                  type="number"
                  min="1"
                  value={it.quantity ?? 1}
                  onChange={e => handleChange(idx, 'quantity', parseInt(e.target.value) || 1)}
                  disabled={disabled}
                  style={{ ...inputStyle, textAlign: 'center', padding: '4px 4px' }}
                />
              </td>
              <td style={cellStyle}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={it.unit_value ?? 0}
                  onChange={e => handleChange(idx, 'unit_value', parseFloat(e.target.value) || 0)}
                  disabled={disabled}
                  style={{ ...inputStyle, textAlign: 'right', padding: '4px 4px' }}
                />
              </td>
              <td style={cellStyle}>
                <select
                  value={it.condition || 'NUEVO'}
                  onChange={e => handleChange(idx, 'condition', e.target.value)}
                  disabled={disabled}
                  style={{ ...inputStyle, padding: '3px 4px', fontSize: '12px' }}
                >
                  <option value="NUEVO">NUEVO</option>
                  <option value="USADO">USADO</option>
                </select>
              </td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => handleDelete(idx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red, #dc2626)', padding: '2px' }}
                  title="Eliminar línea"
                >
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={handleAdd}
        disabled={disabled}
        style={{
          marginTop: '6px', fontSize: '12px', color: 'var(--blue)',
          background: 'none', border: '1px dashed var(--blue)', borderRadius: '6px',
          padding: '4px 12px', cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: '4px',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <Plus size={12} /> Agregar línea
      </button>
    </div>
  );
}

// ── Tax summary row ───────────────────────────────────────────────────────────
function TaxSummary({ items, config }) {
  const t = deckTaxes(items, config);
  if (!t) {
    return (
      <div style={{ marginTop: '10px', padding: '8px 12px', background: 'var(--red-soft, #fef2f2)', borderRadius: '8px', fontSize: '12px', color: 'var(--avianca-red, #991b1b)' }}>
        Falta la configuración aduanal para este tipo — no se puede calcular el impuesto.
      </div>
    );
  }
  return (
    <div style={{ marginTop: '10px', padding: '8px 12px', background: 'var(--bg-soft, #f8f9fa)', borderRadius: '8px', fontSize: '12px' }}>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', color: 'var(--text-secondary)' }}>
        <span>FOB: <strong>${fmt(t.fob)}</strong></span>
        <span>R: <strong>${fmt(t.r)}</strong></span>
        <span>IVA: <strong>${fmt(t.iva)}</strong></span>
        <span>DAI: <strong>${fmt(t.dai)}</strong></span>
        <span style={{ fontWeight: 700, color: 'var(--text)' }}>
          Total impuestos: <strong>${fmt(t.total)}</strong>
        </span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const DeclarationReview = ({ user, userPermissions }) => {
  const [manifests, setManifests]         = useState([]);
  const [manifLoading, setManifLoading]   = useState(true);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [deckMap, setDeckMap]             = useState({});  // key → { label, receipts, type, weight_lb }
  const [editMap, setEditMap]             = useState({});  // key → items[]
  const [expanded, setExpanded]           = useState({});  // key → bool
  const [analyzing, setAnalyzing]         = useState(new Set());
  const [customsConfigs, setCustomsConfigs] = useState({ casillero: null, encomienda: null });
  const [deckLoading, setDeckLoading]     = useState(false);
  const [showAnexoPreview, setShowAnexoPreview] = useState(false);
  const [toast, setToast]                 = useState('');
  const [saving, setSaving]               = useState(false);
  const [draftMeta, setDraftMeta]         = useState(null);
  const [newDeckKeys, setNewDeckKeys]     = useState([]);
  const [isConfirmed, setIsConfirmed]     = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  // ── Fetch closed manifests ──────────────────────────────────────────────────
  /* eslint-disable */
  useEffect(() => {
    const load = async () => {
      setManifLoading(true);
      try {
        const { data } = await supabase
          .from('manifests')
          .select('id, closed_at, closed_by')
          .eq('status', 'CLOSED')
          .order('closed_at', { ascending: false });
        setManifests(data || []);
      } catch (err) {
        console.error('[declLoad] manifests fetch error:', err);
        setManifests([]);
      } finally {
        setManifLoading(false);
      }
    };
    load();
  }, []);
  /* eslint-enable */

  // ── Fetch customs config — one row per tipo, no defaults ────────────────────
  // A missing row leaves that tipo null: its decks show no tax and confirming
  // is blocked.
  useEffect(() => {
    Promise.all([
      fetchCustomsConfigRow(supabase, 'casillero'),
      fetchCustomsConfigRow(supabase, 'encomienda'),
    ]).then(([casillero, encomienda]) => setCustomsConfigs({ casillero, encomienda }));
  }, []);

  // ── Load decks when manifest changes ───────────────────────────────────────
  /* eslint-disable */
  const loadReceiptsForManifest = useCallback(async (manifest) => {
    if (!manifest) return;
    setDeckLoading(true);
    setExpanded({});
    let draft = null;
    try {
      const { data: draftData } = await supabase
        .from('declaration_drafts')
        .select('payload, status, confirmed_at, confirmed_by')
        .eq('manifest_id', manifest.id)
        .maybeSingle();
      draft = draftData;

      if (draft?.payload?.packages?.length > 0) {
        const restored = {};
        for (const pkg of draft.payload.packages) {
          restored[pkg.deck_key] = {
            items:     pkg.items    ?? [],
            label:     pkg.label    ?? pkg.deck_key,
            taxes:     pkg.taxes    ?? {},
            weight_lb: pkg.weight_lb ?? null,
          };
        }
        setEditMap(restored);
        setDraftMeta({ status: draft.status, confirmed_at: draft.confirmed_at, confirmed_by: draft.confirmed_by });
        setIsConfirmed(draft.status === 'confirmed');
        console.log('[declLoad] draft restored — manifest:', manifest.id, 'packages:', draft.payload.packages.length);
      } else {
        setEditMap({});
        setDraftMeta(null);
        setIsConfirmed(false);
      }

      console.log('[declLoad] loading receipts for manifest:', manifest.id);
      const { data: receipts } = await supabase
        .from('package_receipts')
        .select('id, receipt_url, encomienda_package_id, shipment_id, uploaded_at, source')
        .eq('manifest_id', manifest.id)
        .order('uploaded_at', { ascending: true });

      const allReceipts = receipts || [];

      // Group by encomienda_package_id first, then shipment_id
      const groups = {};
      for (const r of allReceipts) {
        const key = r.encomienda_package_id
          ? 'enc-' + r.encomienda_package_id
          : r.shipment_id
            ? 'ship-' + r.shipment_id
            : 'unk-' + r.id;
        if (!groups[key]) {
          groups[key] = {
            type: r.encomienda_package_id ? 'encomienda' : 'casillero',
            rawId: r.encomienda_package_id || r.shipment_id,
            receipts: [],
          };
        }
        groups[key].receipts.push(r);
      }

      // Fetch human labels
      const encKeys = Object.entries(groups)
        .filter(([, v]) => v.type === 'encomienda')
        .map(([, v]) => v.rawId)
        .filter(Boolean);
      const shipKeys = Object.entries(groups)
        .filter(([, v]) => v.type === 'casillero')
        .map(([, v]) => v.rawId)
        .filter(Boolean);

      const labelMap = {};

      if (encKeys.length) {
        const { data: pkgs } = await supabase
          .from('encomienda_packages')
          .select('id, package_number, recipient_name, unique_package_id')
          .in('id', encKeys);
        for (const p of (pkgs || [])) {
          labelMap['enc-' + p.id] = {
            label: `${p.recipient_name || 'Sin nombre'} - ${p.unique_package_id || ('PKG-' + p.package_number)}`,
            weight_lb: 0, // weight_lb not on encomienda_packages — using 0 until weight is stored per package
          };
        }
      }

      if (shipKeys.length) {
        const { data: ships } = await supabase
          .from('shipments')
          .select('id, unique_package_id, customer_id, weight_lb')
          .in('id', shipKeys);
        for (const s of (ships || [])) {
          labelMap['ship-' + s.id] = {
            label: s.unique_package_id || s.customer_id || s.id,
            weight_lb: s.weight_lb || null,
          };
        }
      }

      const newDeckMap = {};
      for (const [key, g] of Object.entries(groups)) {
        newDeckMap[key] = {
          ...g,
          label: labelMap[key]?.label || key,
          weight_lb: labelMap[key]?.weight_lb || null,
        };
      }

      setDeckMap(newDeckMap);

      // Check whether the confirmed payload actually contains encomienda items.
      // Legacy drafts confirmed before the inject was wired correctly may have
      // empty stub entries — those need a one-time repair injection.
      const payloadHasEncItems = (draft?.payload?.packages || []).some(
        p => (p.deck_key || '').startsWith('enc-') && (p.items?.length || 0) > 0
      );
      const shouldInject = draft?.status !== 'confirmed' || !payloadHasEncItems;

      if (shouldInject) {
        if (draft?.status === 'confirmed') {
          console.log('[declLoad] confirmed draft missing enc items — running repair inject');
        }
      // (encomienda_package_id is null on all receipt rows, so enc packages are invisible)
      try {
        const { data: encPkgs } = await supabase
          .from('encomienda_packages')
          .select('id, recipient_name, unique_package_id, package_number')
          .eq('entry_manifest_id', manifest.id)
          .order('package_number', { ascending: true });

        // Fetch all encomienda_items for these packages in one query
        const encPkgIds = (encPkgs || []).map(p => p.id);
        let encItemMap = {}; // { [package_id]: items[] }
        if (encPkgIds.length > 0) {
          const { data: encItems } = await supabase
            .from('encomienda_items')
            .select('id, package_id, description, quantity, unit_value, condition')
            .in('package_id', encPkgIds);
          for (const item of (encItems || [])) {
            if (!encItemMap[item.package_id]) encItemMap[item.package_id] = [];
            encItemMap[item.package_id].push({
              id: 'enc-item-' + item.id,
              description: item.description || '',
              product_name: item.description || '',
              quantity: parseInt(item.quantity) || 1,
              unit_value: parseFloat(item.unit_value) || 0,
              condition: item.condition || 'NUEVO',
              is_merchandise: true,
            });
          }
          console.log('[declLoad] encomienda items loaded:', (encItems || []).length, 'items for', encPkgIds.length, 'packages');
        }

        // Fetch receipts uploaded via wizard (encomienda_package_id is set for these)
        let encReceiptMap = {}; // { [package_id]: receipts[] }
        if (encPkgIds.length > 0) {
          const { data: encReceipts } = await supabase
            .from('package_receipts')
            .select('id, receipt_url, encomienda_package_id, uploaded_at, source')
            .in('encomienda_package_id', encPkgIds)
            .order('uploaded_at', { ascending: true });
          for (const r of (encReceipts || [])) {
            if (!encReceiptMap[r.encomienda_package_id]) encReceiptMap[r.encomienda_package_id] = [];
            encReceiptMap[r.encomienda_package_id].push(r);
          }
          console.log('[declLoad] encomienda receipts loaded:', (encReceipts || []).length, 'receipts');
        }

        for (const pkg of (encPkgs || [])) {
          const key = 'enc-' + pkg.id;
          const pkgReceipts = encReceiptMap[pkg.id] || [];
          if (!newDeckMap[key]) {
            newDeckMap[key] = {
              type: 'encomienda',
              rawId: pkg.id,
              receipts: pkgReceipts,
              label: `${pkg.recipient_name || 'Sin nombre'} - ${pkg.unique_package_id || ('PKG-' + pkg.package_number)}`,
              weight_lb: null,
            };
          } else {
            // Deck already exists from receipt grouping — enrich with wizard receipts if any
            if (pkgReceipts.length > 0) {
              newDeckMap[key].receipts = [...(newDeckMap[key].receipts || []), ...pkgReceipts];
            }
          }

          // Pre-populate editMap with wizard items if not already in draft
          const wizardItems = encItemMap[pkg.id];
          if (wizardItems && wizardItems.length > 0 && !editMap[key]) {
            setEditMap(prev => ({ ...prev, [key]: wizardItems }));
          }
        }

        if ((encPkgs || []).length > 0) {
          setDeckMap({ ...newDeckMap });
          console.log('[declLoad] encomienda packages injected:', encPkgs.length);
        }
      } catch (encErr) {
        console.warn('[declLoad] Could not inject encomienda packages:', encErr);
      }
      } else {
        // Payload already complete — restore enc packages from payload into deckMap.
        // editMap was already restored at the top of this function with all 59 packages.
        // We only need to add enc deck entries to deckMap so the UI renders them
        // (enc packages without receipts are never in the receipt-based newDeckMap).
        const encPackagesFromPayload = (draft?.payload?.packages || []).filter(
          p => (p.deck_key || '').startsWith('enc-')
        );
        console.log('[declLoad] restoring', encPackagesFromPayload.length,
                    'enc packages from confirmed payload');

        for (const p of encPackagesFromPayload) {
          if (!newDeckMap[p.deck_key]) {
            newDeckMap[p.deck_key] = {
              type: 'encomienda',
              rawId: p.deck_key.replace('enc-', ''),
              receipts: [],
              label: p.label || p.deck_key,
              weight_lb: p.weight_lb || null,
            };
          }
        }

        if (encPackagesFromPayload.length > 0) {
          setDeckMap({ ...newDeckMap });
        }

        console.log('[declLoad] draft is confirmed and payload complete — encomienda packages restored from payload');
      }

      // Compare live decks against saved draft to detect new packages
      if (draft?.payload?.packages) {
        const savedKeys = new Set(draft.payload.packages.map(p => p.deck_key));
        const liveKeys  = Object.keys(newDeckMap);
        const newDecks  = liveKeys.filter(k => !savedKeys.has(k));
        if (newDecks.length > 0) {
          setNewDeckKeys(newDecks);
          console.log('[declLoad] new packages detected since last save:', newDecks.length, newDecks);
        } else {
          setNewDeckKeys([]);
        }
      } else {
        setNewDeckKeys([]);
      }
    } catch (err) {
      console.error('[declLoad] deck load error:', err);
      setDeckMap({});
    } finally {
      setDeckLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedManifest) {
      setDeckMap({});
      setEditMap({});
      setExpanded({});
      setDraftMeta(null);
      setIsConfirmed(false);
      setNewDeckKeys([]);
      return;
    }
    loadReceiptsForManifest(selectedManifest);
  }, [selectedManifest?.id]);
  /* eslint-enable */

  // ── Analyze receipts via swift-endpoint ────────────────────────────────────
  const handleAnalyze = useCallback(async (deckKey) => {
    const deck = deckMap[deckKey];
    if (!deck) return;
    const imageUrls = (deck.receipts || []).map(r => r.receipt_url).filter(Boolean);
    if (!imageUrls.length) { showToast('Este paquete no tiene recibos para analizar.'); return; }

    console.log('[declLoad] analyze start:', deckKey, 'images:', imageUrls.length);
    setAnalyzing(prev => new Set([...prev, deckKey]));
    setExpanded(prev => ({ ...prev, [deckKey]: true }));

    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const anonKey     = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/swift-endpoint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ imageUrls }),
      });

      const data = await res.json();

      if (data.fallback) {
        console.log('[declLoad] analyze fallback — manual-mode activated:', deckKey);
        showToast('Análisis no disponible. Modo manual activado.');
        setEditMap(prev => ({ ...prev, [deckKey]: [emptyItem()] }));
        return;
      }

      const text = (data.content || [])
        .filter(b => b.type === 'text')
        .map(b => b.text)
        .join('');
      const clean = text.replace(/```json/g, '').replace(/```/g, '').trim();
      if (!clean) {
        console.error('[declLoad] analyze error: empty response from Vision API for deck:', deckKey);
        throw new Error('Empty response from Vision API');
      }
      const result = JSON.parse(clean);
      const items = (result.items || []).map((it, i) => ({
        id: 'ai-' + Date.now() + '-' + i,
        // Compressed format fields (name/qty/price) with verbose fallbacks for backward compat
        description: it.name ?? it.description ?? it.descripcion ?? it.desc ?? '',
        product_name: it.name ?? it.product_name ?? it.nombre ?? '',
        quantity: parseInt(it.qty ?? it.quantity) || 1,
        unit_value: parseFloat(it.price ?? it.unit_price ?? it.unit_value ?? 0) || 0,
        condition: it.condition || 'NUEVO',
        is_merchandise: false,
      }));

      console.log('[declLoad] analyze complete:', deckKey, 'items:', items.length);
      setEditMap(prev => ({ ...prev, [deckKey]: items.length ? items : [emptyItem()] }));
    } catch (err) {
      console.error('[declLoad] analyze error:', deckKey, err);
      showToast('Error al analizar. Intente de nuevo.');
      setEditMap(prev => ({ ...prev, [deckKey]: [emptyItem()] }));
    } finally {
      setAnalyzing(prev => { const s = new Set(prev); s.delete(deckKey); return s; });
    }
  }, [deckMap]);

  // ── Manual mode ────────────────────────────────────────────────────────────
  const handleManualMode = (deckKey) => {
    console.log('[declLoad] manual-mode activated:', deckKey);
    setEditMap(prev => ({ ...prev, [deckKey]: [emptyItem()] }));
    setExpanded(prev => ({ ...prev, [deckKey]: true }));
  };

  // ── Confirm / save declaration ─────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!selectedManifest) return;
    console.log('[declLoad] confirm click — manifest:', selectedManifest.id, 'decks with data:', Object.keys(editMap).length);
    if (totals.configMissing) {
      showToast('Falta la configuración aduanal — no se puede confirmar la declaración.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        manifest_id: selectedManifest.id,
        confirmed_by: user?.username || user?.email || 'unknown',
        confirmed_at: new Date().toISOString(),
        customs_config: customsConfigs,
        packages: Object.entries(editMap).map(([key, rawVal]) => {
          const itemsArr = Array.isArray(rawVal) ? rawVal : (rawVal?.items || []);
          return {
            deck_key: key,
            label: deckMap[key]?.label || key,
            weight_lb: deckMap[key]?.weight_lb || null,
            items: itemsArr,
            taxes: deckTaxes(itemsArr, customsConfigs[deckTipo(key, deckMap[key])]),
          };
        }),
      };

      const { error } = await supabase
        .from('declaration_drafts')
        .upsert({
          manifest_id:     selectedManifest.id,
          payload:         payload,
          total_fob:       totals.fob        ?? 0,
          total_dai:       totals.dai        ?? 0,
          total_iva:       totals.iva        ?? 0,
          vts_fee:         customsConfigs.casillero?.vts_fee ?? 15.93,
          its_fee:         customsConfigs.casillero?.its_fee ?? 2.07,
          total_importe:   totals.total      ?? 0,
          config_snapshot: customsConfigs,
          status:          'confirmed',
          confirmed_by:    user?.username    ?? user?.email ?? 'unknown',
          confirmed_at:    new Date().toISOString(),
        }, { onConflict: 'manifest_id' });

      if (error) throw error;

      showToast('Declaración guardada.');
    } catch (err) {
      console.error('[declLoad] confirm error:', err);
      showToast('Error al guardar. Intente de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  // ── Aggregate totals ────────────────────────────────────────────────────────
  const allDecks    = Object.keys(deckMap);
  const analyzedQty = Object.keys(editMap).length;
  const totals = allDecks.reduce((acc, key) => {
    const rawVal = editMap[key];
    const itemsArr = Array.isArray(rawVal) ? rawVal : (rawVal?.items || []);
    // Each deck is taxed on its own FOB; only the results are summed.
    const t = deckTaxes(itemsArr, customsConfigs[deckTipo(key, deckMap[key])]);
    if (!t) return { ...acc, configMissing: true };
    const c = (n) => Math.round(n * 100) / 100;
    return { ...acc, fob: c(acc.fob + t.fob), dai: c(acc.dai + t.dai), iva: c(acc.iva + t.iva), total: c(acc.total + t.total) };
  }, { fob: 0, dai: 0, iva: 0, total: 0, configMissing: false });

  // ── Permission guard ────────────────────────────────────────────────────────
  if (!userPermissions?.canExportCosteo) {
    return (
      <div className="tile max-w-5xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <AlertCircle size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
        <span className="metric-label">No tienes permisos para acceder a esta sección.</span>
      </div>
    );
  }

  if (manifLoading) {
    return (
      <div className="tile max-w-5xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <Loader size={24} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
        <span className="metric-label">Cargando manifiestos…</span>
      </div>
    );
  }

  const handleReEdit = async () => {
    if (!window.confirm('¿Deseas re-abrir esta declaración para editar? Deberás confirmar nuevamente al terminar.')) return;
    const { error } = await supabase
      .from('declaration_drafts')
      .update({ status: 'draft', confirmed_at: null, confirmed_by: null })
      .eq('manifest_id', selectedManifest?.id);
    if (error) {
      console.error('[declReEdit] failed to unlock draft:', error);
      alert('Error al re-abrir la declaración.');
      return;
    }
    console.log('[declReEdit] draft unlocked for manifest:', selectedManifest?.id);
    setIsConfirmed(false);
    await loadReceiptsForManifest(selectedManifest);
  };

  return (
    <div className="max-w-5xl mx-auto" style={{ paddingBottom: '120px' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: '10px', padding: '12px 20px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          fontSize: '14px', fontWeight: 500,
        }}>
          {toast}
        </div>
      )}

      {/* Header + manifest selector */}
      <div className="tile" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Declaración Aduanera</h2>
            <p className="metric-sub" style={{ margin: '4px 0 0' }}>
              Analiza recibos y genera la declaración DUA para SIDUNEA.
            </p>
          </div>
          <FileText size={28} style={{ color: 'var(--blue)', opacity: 0.6 }} />
        </div>

        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Manifiesto:</label>
          <div style={{ position: 'relative', flex: 1, maxWidth: '340px' }}>
            <select
              value={selectedManifest?.id || ''}
              onChange={e => {
                const m = (manifests || []).find(x => x.id === e.target.value) || null;
                setSelectedManifest(m);
              }}
              style={{
                width: '100%', padding: '8px 32px 8px 12px', fontSize: '14px',
                border: '1px solid var(--border)', borderRadius: '8px',
                background: 'var(--bg)', appearance: 'none', cursor: 'pointer',
              }}
            >
              <option value="">— Seleccionar manifiesto cerrado —</option>
              {(manifests || []).map(m => {
                const d = m.closed_at
                  ? new Date(m.closed_at).toLocaleDateString('es-SV', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '';
                return (
                  <option key={m.id} value={m.id}>
                    {m.id}{d ? ` · ${d}` : ''}
                  </option>
                );
              })}
            </select>
            <ChevronDown size={14} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-secondary)' }} />
          </div>
          <button
            onClick={() => { if (selectedManifest) loadReceiptsForManifest(selectedManifest); }}
            title="Recargar paquetes"
            style={{ marginLeft: 8 }}
          >
            🔄 Actualizar
          </button>
          {deckLoading && <Loader size={16} style={{ opacity: 0.5 }} />}
        </div>

        {/* Anexo de Aduanas — view-only PDF preview (below the Actualizar row) */}
        {selectedManifest && (
          <div style={{ marginTop: '12px' }}>
            <button
              type="button"
              onClick={() => setShowAnexoPreview(prev => !prev)}
              className="btn btn-secondary"
              style={{
                fontSize: '13px',
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                borderColor: 'var(--blue)',
                color: 'var(--blue)',
              }}
            >
              👁 {showAnexoPreview ? 'Ocultar Vista Previa Anexo' : 'Vista Previa Anexo'}
            </button>

            {showAnexoPreview && (
              <div style={{ marginTop: '12px' }}>
                <AnexoPreview manifestId={selectedManifest.id} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Empty states */}
      {!selectedManifest && (
        <div className="tile" style={{ padding: '40px', textAlign: 'center' }}>
          <FileText size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <span className="metric-label">Selecciona un manifiesto para comenzar.</span>
        </div>
      )}

      {selectedManifest && !deckLoading && allDecks.length === 0 && (
        <div className="tile" style={{ padding: '32px', textAlign: 'center' }}>
          <span className="metric-label">Este manifiesto no tiene recibos cargados.</span>
        </div>
      )}

      {/* Package deck grid */}
      {selectedManifest && !deckLoading && allDecks.length > 0 && (
        <>
          {draftMeta && (
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 14px', marginBottom: 12, fontSize: 13 }}>
              ✓ Borrador guardado — {draftMeta.status} · {draftMeta.confirmed_by} · {new Date(draftMeta.confirmed_at).toLocaleString()}
              <span style={{ marginLeft: 16, color: '#555' }}>
                {Object.keys(editMap).length} / {allDecks.length} paquetes analizados
              </span>
            </div>
          )}
          {newDeckKeys.length > 0 && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 14px', marginBottom: 12, fontSize: 13 }}>
              ⚠️ {newDeckKeys.length} paquete(s) nuevo(s) desde la última confirmación — analícelos antes de confirmar
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {allDecks.map(key => {
            const deck         = deckMap[key];
            const items        = editMap[key]?.items || editMap[key] || [];
            const isAnalyzing  = analyzing.has(key);
            const isParsed     = items.length > 0;
            const isExpanded   = !!expanded[key];
            const receiptCount = (deck.receipts || []).length;

            return (
              <div key={key} className="tile" style={{ padding: 0, overflow: 'hidden', borderLeft: newDeckKeys.includes(key) ? '4px solid #ffc107' : '4px solid transparent' }}>
                {/* Card header — toggle expand */}
                <button
                  type="button"
                  onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                  style={{
                    width: '100%', padding: '14px 18px',
                    background: 'transparent', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {isParsed
                      ? <CheckCircle size={18} style={{ color: 'var(--green, #16a34a)', flexShrink: 0 }} />
                      : <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2px solid var(--border)', flexShrink: 0 }} />
                    }
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px' }}>{deck.label}</div>
                      <div className="metric-sub">
                        {receiptCount} recibo{receiptCount !== 1 ? 's' : ''}
                        {deck.weight_lb ? ` · ${deck.weight_lb} lb / ${lbToKg(deck.weight_lb)} kg` : ''}
                        {' · '}{deck.type === 'encomienda' ? 'ENCOMIENDA' : 'CASILLERO'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isParsed && (
                      <span className="badge" style={{ background: 'var(--green-soft, #dcfce7)', color: 'var(--green, #16a34a)', fontSize: '11px' }}>
                        ✓ {items.length} línea{items.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span style={{ fontSize: '18px', color: 'var(--text-tertiary)' }}>
                      {isExpanded ? '▾' : '▸'}
                    </span>
                  </div>
                </button>

                {/* Expanded panel */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '14px 18px', background: 'var(--bg)' }}>
                    {/* Receipt thumbnails */}
                    {receiptCount > 0 && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        {(deck.receipts || []).map(r => (
                          <a key={r.id} href={r.receipt_url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={r.receipt_url}
                              alt="recibo"
                              style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'zoom-in' }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => handleAnalyze(key)}
                        disabled={isAnalyzing || receiptCount === 0}
                        className="btn btn-primary"
                        style={{ fontSize: '13px', padding: '7px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        {isAnalyzing
                          ? <><Loader size={13} /> Analizando…</>
                          : <><RefreshCw size={13} /> {isParsed ? 'Re-analizar' : 'Analizar'}</>
                        }
                      </button>
                      {!isParsed && (
                        <button
                          type="button"
                          onClick={() => handleManualMode(key)}
                          className="btn btn-secondary"
                          style={{ fontSize: '13px', padding: '7px 14px' }}
                        >
                          Ingresar manual
                        </button>
                      )}
                    </div>

                    {/* DUA item lines + tax summary */}
                    {isParsed && (
                      <>
                        <ItemTable
                          items={items}
                          onChange={updated => setEditMap(prev => ({ ...prev, [key]: updated }))}
                          disabled={isConfirmed}
                        />
                        <TaxSummary items={items} config={customsConfigs[deckTipo(key, deck)]} />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}

      {/* Sticky totals footer */}
      {selectedManifest && allDecks.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--bg)', borderTop: '2px solid var(--border)',
          padding: '14px 24px', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: '24px', fontSize: '13px', flexWrap: 'wrap' }}>
            <span>
              <span className="metric-sub">Analizados:</span>{' '}
              <strong>{analyzedQty} / {allDecks.length}</strong>
            </span>
            <span>
              <span className="metric-sub">FOB total:</span>{' '}
              <strong>${fmt(totals.fob)}</strong>
            </span>
            <span>
              <span className="metric-sub">DAI:</span>{' '}
              <strong>${fmt(totals.dai)}</strong>
            </span>
            <span>
              <span className="metric-sub">IVA:</span>{' '}
              <strong>${fmt(totals.iva)}</strong>
            </span>
            <span style={{ fontWeight: 700, fontSize: '14px' }}>
              Total impuestos: ${fmt(totals.total)}
            </span>
          </div>
          {isConfirmed && (
            <button
              onClick={handleReEdit}
              style={{
                marginRight: '12px',
                padding: '10px 20px',
                background: 'transparent',
                border: '2px solid #e53e3e',
                color: '#e53e3e',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Re-editar declaración
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || analyzedQty === 0}
            className="btn btn-primary"
            style={{ fontSize: '14px', padding: '10px 24px', minWidth: '180px', justifyContent: 'center' }}
          >
            {saving ? 'Guardando…' : 'Confirmar declaración'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DeclarationReview;
